import type { StudyState } from '@sigora/domain';
import { useEffect, useState } from 'react';

import type { Study } from '../api/client.ts';
import {
  FIELDING_AT_TARGET_HEADLINE,
  FIELDWORK_CLOSING_LINE,
  FIELDWORK_FINISHED,
} from '../artifacts/launch-copy.ts';
import { PhaseStrip } from '../artifacts/PhaseStrip.tsx';
import { describeProgress } from '../artifacts/progress-card.ts';
import { isLaunchedState } from '../artifacts/state.ts';
import { useBoundedPoll } from '../artifacts/use-bounded-poll.ts';
import { Pending } from '../ui/Pending.tsx';
import { STUDY_COMPLETE_LINE } from './brief-copy.ts';

/**
 * What a study says about itself once it is past the launch.
 *
 * Every state after `live` has a sentence of its own: a finished study is the end of the run, and
 * the thing anyone watching is waiting for, so no launched state reads as though nothing had
 * happened. Plain language, and nothing about who fielded it.
 *
 * Fielding on an `analyzing` or `complete` study is a step already behind it, opened by going back:
 * it says fieldwork is over, and the Analyzing step says what is happening to the answers.
 */
const LAUNCHED_STATE_MESSAGES: Partial<Record<StudyState, string>> = {
  live: 'This study is live. Participants are answering it now.',
  analyzing: FIELDWORK_FINISHED,
  complete: STUDY_COMPLETE_LINE,
  under_recruited: 'This study finished, but with fewer participants than it asked for.',
  cancelled: 'This study was stopped before it finished.',
};

/**
 * How often a live study is re-read.
 *
 * Longer than Launch's set-up poll, not shorter: that one is watching for a state change it wants
 * to catch the moment it happens, while this one is re-reading a number the server itself only
 * refreshes every ten seconds. Anything faster than the server's own interval would be asking
 * more often than there is anything new to hear; five seconds keeps the card at most one server
 * poll behind.
 */
export const LIVE_POLL_MS = 5_000;

/**
 * How long to keep watching a live study before leaving the card where it stands.
 *
 * Half an hour, which is far longer than the simulated fielding this environment does and long
 * enough that reaching it means something is wrong. The card is not withdrawn when it does — the
 * last reading was real and stays on screen — it just stops claiming to be current.
 */
export const LIVE_POLL_LIMIT = 360;

/** How often the fielding time and the reading's age on the progress card are worked out again. */
const AGE_TICK_MS = 30_000;

export interface FieldingStepProps {
  study: Study | null;
  /**
   * Asks the shell to read the study again. The watch calls it, and the shell's read is what moves
   * the researcher on to Analyzing once fieldwork is over.
   */
  onStudyChanged: () => void;
}

/**
 * What a launched study is doing in fieldwork: where it is, and how many have finished.
 *
 * The step watches the study while it is `live`, by asking the shell to read it again. Those reads
 * are what move the researcher on to Analyzing once the server says fieldwork is over, so a study
 * opened on this step is watched from the moment it renders, and leaving the step stops the watch
 * with it. Once the study is past `live` nothing here moves, so nothing is watched.
 */
export const FieldingStep = ({ study, onStudyChanged }: FieldingStepProps) => {
  const [now, setNow] = useState(() => new Date());
  const state = study?.state ?? null;
  const launched = state !== null && isLaunchedState(state);

  useEffect(() => {
    // Only the live card says ages that move with the clock, so only a live study keeps one.
    if (state !== 'live') {
      return undefined;
    }

    const timer = setInterval(() => setNow(new Date()), AGE_TICK_MS);

    return () => clearInterval(timer);
  }, [state]);

  /**
   * Fielding, watched as participants answer.
   *
   * Only `live`, and bounded: a page left open on a study that never reports its fieldwork over
   * stops asking after `LIVE_POLL_LIMIT` reads and says so. The analysis that follows is watched on
   * the Analyzing step, by a watch of its own that the server's endings bound instead.
   */
  const watchedTooLong = useBoundedPoll({
    active: state === 'live',
    intervalMs: LIVE_POLL_MS,
    limit: LIVE_POLL_LIMIT,
    onTick: onStudyChanged,
  });

  // Rendered for `live` and for `live` only: the counts are what fielding is doing right now,
  // and a card left up beside "this study is finished" would be a frozen number under a
  // sentence that has moved past it.
  const card =
    study !== null && state === 'live'
      ? describeProgress({
          progress: study.remeshProgress,
          launchedAt: study.launchedAt === null ? null : new Date(study.launchedAt),
          now,
        })
      : null;
  /** The live watch's giving-up, said only while the study is still `live`, the state it watched. */
  const stoppedWatching = watchedTooLong && state === 'live';
  /**
   * A `live` study whose card says everyone it asked for has finished (`card.atTarget`, which
   * already leaves out a stale reading), while the page is still checking: once it has stopped,
   * the count may no longer be the study's. It changes the headline and adds one line under the
   * card saying fieldwork is closing, and nothing else: the study is still `live` until the server
   * moves it on, and a count that drops below the target (a returned submission) undoes both.
   */
  const atTarget = state === 'live' && card !== null && card.atTarget && !stoppedWatching;

  if (study === null || state === null || !launched) {
    return null;
  }

  const headline = atTarget ? FIELDING_AT_TARGET_HEADLINE : LAUNCHED_STATE_MESSAGES[state];

  return (
    <section className="launch-panel fielding-step" data-test-id="fielding-step">
      <PhaseStrip settingUp={false} state={state} />

      {headline !== undefined && <p data-test-id="launch-launched">{headline}</p>}

      {/*
          Outside the progress card, which renders for `live` alone, so the page's own giving-up is
          said whichever state is rendered rather than only in the one where the card exists.
        */}
      {stoppedWatching && (
        <p className="chat-error" data-test-id="launch-progress-stopped">
          This page has stopped checking. The reading below is the last one it took — reload to
          start again.
        </p>
      )}

      {card !== null && (
        <div className="launch-progress" data-test-id="launch-progress">
          {/*
            The card's one count line, muted with the card once its reading is stale. A screen
            reader hears the full sentence, which names participants, in place of the short form.
          */}
          {card.heroLabel !== null && (
            <p
              className={
                card.stale
                  ? 'launch-progress-hero launch-progress-hero-stale'
                  : 'launch-progress-hero'
              }
              data-test-id="launch-progress-hero"
            >
              <span aria-hidden="true" data-test-id="launch-progress-hero-label">
                {card.heroLabel}
              </span>
              <span className="visually-hidden">{card.heroSentence}</span>
            </p>
          )}
          {card.percent !== null && (
            <div className="launch-progress-track">
              <div
                className="launch-progress-bar"
                data-test-id="launch-progress-bar"
                style={{ width: `${String(card.percent)}%` }}
              />
            </div>
          )}
          {/* Why there is no count, in place of the hero: no reading yet, or none reported. */}
          {card.countLabel !== null && (
            <p data-test-id="launch-progress-count">{card.countLabel}</p>
          )}
          {card.timingLabel !== null && (
            <p data-test-id="launch-progress-timing">{card.timingLabel}</p>
          )}
          {card.readingLabel !== null && (
            <p
              className={card.stale ? 'chat-error' : undefined}
              data-test-id="launch-progress-read-at"
            >
              {card.readingLabel}
            </p>
          )}
        </div>
      )}

      {atTarget && (
        <Pending className="fielding-closing" testId="fielding-closing">
          {FIELDWORK_CLOSING_LINE}
        </Pending>
      )}
    </section>
  );
};
